"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function NapraveetePorachka() {
  const [isOpen, setIsOpen] = useState(false);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section id="направете-поръчка" ref={ref} className={`py-16 md:py-24 bg-linear-to-br from-background via-primary/5 to-background relative overflow-hidden transition-all duration-150 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/food.png')]" />
      <div className="container">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-[32px] md:text-[40px] leading-[1.25] md:leading-[1.2] font-display font-bold tracking-tight mb-6">
            Направете поръчка
          </h2>
          <p className="text-[16px] leading-[1.5] text-muted-foreground mb-8">
            Лесно и бързо поръчайте вашите любими ястия директно през нашата контактна форма.
          </p>
          <Button onClick={() => {
          const formElement = document.getElementById("full_order_form_home");
          if (formElement) {
            formElement.dispatchEvent(new Event("submit", {
              cancelable: true,
              bubbles: true
            }));
          } else {
            setIsOpen(true);
          }
        }} className="btn-primary" size="lg">
            Поръчай сега
          </Button>
        </div>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Направете поръчка</DialogTitle>
            <DialogDescription>
              Попълнете формата по-долу и ние ще се свържем с вас за потвърждение.
            </DialogDescription>
          </DialogHeader>
          <FormProvider formId="full_order_form_home" sectionName="направете-поръчка" defaultValues={{
          customer_name: "",
          phone_number: "",
          email_address: "",
          order_details: "",
          order_type: "",
          address: ""
        }} validationRules={{
          customer_name: {
            required: true,
            minLength: 2,
            maxLength: 100
          },
          phone_number: {
            required: true,
            pattern: "phone"
          },
          email_address: {
            pattern: "email"
          },
          order_details: {
            required: true,
            minLength: 5
          },
          order_type: {
            required: true
          },
          address: {
            required: true
          }
        }} submitText="Изпрати поръчка" successMessage="Благодарим ви! Ще се свържем с вас скоро за потвърждение." errorMessage="Неуспешно изпращане. Моля, опитайте отново.">
            <OrderFormContent onSuccess={() => setIsOpen(false)} />
          </FormProvider>
        </DialogContent>
      </Dialog>
    </section>;
}
function OrderFormContent({
  onSuccess
}: {
  onSuccess: () => void;
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-[hsl(95_50%_45%/0.1)] border border-[hsl(95_50%_45%/0.3)] rounded-lg">
        <p className="text-[hsl(95_50%_35%)] font-semibold mb-4">{successMessage}</p>
        <Button onClick={() => {
        resetForm();
        onSuccess();
      }} variant="outline" className="w-full">
          Затвори
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <FormField control={control} name="customer_name" rules={{
      required: "Името е задължително",
      minLength: {
        value: 2,
        message: "Минимум 2 символа"
      },
      maxLength: {
        value: 100,
        message: "Максимум 100 символа"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Име и фамилия</FormLabel>
            <FormControl>
              <Input placeholder="Иван Петров" {...field} />
            </FormControl>
            <FormDescription>
              Въведете пълното си име
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone_number" rules={{
      required: "Телефонният номер е задължителен",
      pattern: {
        value: /^[+]?[\d\s()-]+$/,
        message: "Невалиден телефонен номер"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Телефонен номер</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+359 88 123 4567" {...field} />
            </FormControl>
            <FormDescription>
              Ще се свържем с вас за потвърждение
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email_address" rules={{
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Невалиден имейл адрес"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Имейл</FormLabel>
            <FormControl>
              <Input type="email" placeholder="ivan@example.com" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="order_details" rules={{
      required: "Моля, опишете поръчката си",
      minLength: {
        value: 5,
        message: "Минимум 5 символа"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Какво желаете да поръчате?</FormLabel>
            <FormControl>
              <Textarea placeholder="Опишете вашата поръчка – ястия, количества, специални изисквания..." rows={4} {...field} />
            </FormControl>
            <FormDescription>
              Колкото по‑подробно, толкова по‑лесно ще обработим поръчката
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="order_type" rules={{
      required: "Моля, изберете тип поръчка"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Тип поръчка</FormLabel>
            <FormControl>
              <select {...field} className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm">
                <option value="" disabled>Изберете тип</option>
                <option value="Доставка">Доставка</option>
                <option value="Вземане от място">Вземане от място</option>
                <option value="На място">На място</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="address" rules={{
      required: "Адресът е задължителен"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Адрес за доставка</FormLabel>
            <FormControl>
              <Input placeholder="гр. София, ул. ..." {...field} />
            </FormControl>
            <FormDescription>
              Въведете точен адрес или уточнение за получаване
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="text-lg font-bold text-primary mb-4 text-center">
        Фиксирана доставка: 2 евро
      </div>

      {submitError && <div className="p-4 bg-[hsl(15_85%_48%/0.1)] border border-[hsl(15_85%_48%/0.3)] rounded-lg">
          <p className="text-[hsl(15_85%_43%)] text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full btn-primary">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Изпращане...
          </> : "Изпрати поръчка"}
      </Button>
    </Form>;
}